import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { RemoveScrollBar as RemoveScrollBarComponent } from "react-remove-scroll-bar";

type RemoveScrollBarProps = {
  removeScrollBar: () => void;
  reinstateScrollBar: () => void;
};

const initialState: RemoveScrollBarProps = {
  removeScrollBar: () => null,
  reinstateScrollBar: () => null,
};

const RemoveScrollBar = createContext<RemoveScrollBarProps>(initialState);

// Track how many components currently need scroll bar removed
// if more than one render <RemoveScrollBar/>
// avoids two <RemoveScrollBar/>s being rendered at once and throwing errors
export const RemoveScrollBarProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [removeScrollBarRequests, setRemoveScrollBarRequests] =
    useState<number>(0);

  function removeScrollBar() {
    setRemoveScrollBarRequests(
      (removeScrollBarRequests) => removeScrollBarRequests + 1
    );
  }

  function reinstateScrollBar() {
    setRemoveScrollBarRequests((removeScrollBarRequests) =>
      removeScrollBarRequests <= 0 ? 0 : removeScrollBarRequests - 1
    );
  }

  const memoedValue = useMemo(
    () => ({
      removeScrollBar,
      reinstateScrollBar,
    }),
    [removeScrollBar, reinstateScrollBar]
  );

  return (
    <RemoveScrollBar.Provider value={memoedValue}>
      {children}
      {removeScrollBarRequests > 0 && <RemoveScrollBarComponent />}
    </RemoveScrollBar.Provider>
  );
};

export default function useRemoveScrollBar() {
  return useContext(RemoveScrollBar);
}
